import { pipe } from 'ramda';
import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { TimesheetExcludedUsersApi } from 'api/timesheets/excluded-users/api';
import { ApiTimesheetExcludedUsers } from 'api/timesheets/excluded-users/types';
import {
  empty,
  loaded,
  loading,
} from 'components/pages/timesheet-settings/excluded-users-settings/core';
import {
  TimesheetExcludedUserMap,
  TimesheetExcludedUsers,
  TimesheetExcludedUserState,
} from 'components/pages/timesheet-settings/excluded-users-settings/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { useUser } from 'hooks/use-user';
import { isUserExcludedFromTimesheet } from './utils';

type TimesheetExcludedUsersKey = [string, string];
export const getTimesheetExcludedUsersKey = (): TimesheetExcludedUsersKey => [
  'timesheet',
  'excluded-users',
];
const apiResponseAsUserMap = ({
  userIds,
}: ApiTimesheetExcludedUsers): TimesheetExcludedUserMap =>
  userIds.reduce(
    (r, id) => ({ ...r, [id]: TimesheetExcludedUserState.Just }),
    {},
  );

type QueryOptionsType = UseQueryOptions<
  TimesheetExcludedUsers,
  unknown,
  TimesheetExcludedUsers,
  TimesheetExcludedUsersKey
>;

export const useTimesheetExcludedUsersQuery = (
  queryOptions: QueryOptionsType = {},
): UseQueryResult<TimesheetExcludedUsers> =>
  useQuery(
    getTimesheetExcludedUsersKey(),
    () =>
      TimesheetExcludedUsersApi.get().then(pipe(apiResponseAsUserMap, loaded)),
    {
      staleTime: GENERAL_API_STALE,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      ...queryOptions,
    },
  );
export const useTimesheetExcludedUsers = (
  queryOptions: QueryOptionsType = {},
): TimesheetExcludedUsers => {
  const { data, isLoading } = useTimesheetExcludedUsersQuery(queryOptions);

  return useMemo(
    () => (isLoading ? loading() : data ?? empty()),
    [data, isLoading],
  );
};

export const useIsCurrentUserExcludedFromTimesheet = (
  queryOptions: QueryOptionsType = {},
): [boolean, boolean] => {
  const exludedUsers = useTimesheetExcludedUsers(queryOptions);
  const {
    user: { userId },
  } = useUser();

  return [
    exludedUsers[0] === 'loading',
    isUserExcludedFromTimesheet(userId)(exludedUsers),
  ];
};
